import ClientJS from 'clientjs';
import logger from 'utils/logger';

const getVideoFullScreenDetails = () => {
  const clientJs = new ClientJS();
  const browserNameRaw = clientJs.getBrowser();
  // Guard against non-string values from clientjs (e.g., null/undefined/object)
  const name = (typeof browserNameRaw === 'string' ? browserNameRaw : '').toLowerCase();
  let requestFullScreen = null;
  let exitFullScreen = null;
  let fullscreenchangeEventName = null;

  // Prefer feature detection over browser-name sniffing. This avoids returning
  // a null event name when clientjs returns an unexpected value.
  try {
    const doc = document;
    const isfullScreenSupported = !!(
      doc.fullscreenEnabled ||
      doc.webkitFullscreenEnabled ||
      doc.mozFullScreenEnabled ||
      doc.msFullscreenEnabled
    );

    if (doc.fullscreenEnabled && doc.documentElement?.requestFullscreen) {
      requestFullScreen = (ref) => ref.requestFullscreen();
      exitFullScreen = () => doc.exitFullscreen?.();
      fullscreenchangeEventName = 'fullscreenchange';
    } else if (
      doc.webkitFullscreenEnabled &&
      doc.documentElement?.webkitRequestFullscreen
    ) {
      requestFullScreen = (ref) => ref.webkitRequestFullscreen();
      exitFullScreen = () => doc.webkitExitFullscreen?.();
      fullscreenchangeEventName = 'webkitfullscreenchange';
    } else if (
      doc.mozFullScreenEnabled &&
      doc.documentElement?.mozRequestFullScreen
    ) {
      requestFullScreen = (ref) => ref.mozRequestFullScreen();
      exitFullScreen = () => doc.mozCancelFullScreen?.();
      fullscreenchangeEventName = 'mozfullscreenchange';
    } else if (
      doc.msFullscreenEnabled &&
      doc.documentElement?.msRequestFullscreen
    ) {
      requestFullScreen = (ref) => ref.msRequestFullscreen();
      exitFullScreen = () => doc.msExitFullscreen?.();
      fullscreenchangeEventName = 'MSFullscreenChange';
    }

    if (isfullScreenSupported && !fullscreenchangeEventName) {
      // Fallback: never return null when fullscreen is supported.
      fullscreenchangeEventName = 'fullscreenchange';
    }

    if (!isfullScreenSupported) {
      logger.warn(
        'browser does not support full screen api',
        'videoscreen.utils.getFullScreenApi',
        { browsername: name }
      );
    }

    return {
      requestFullScreen,
      exitFullScreen,
      isfullScreenSupported,
      browserName: name,
      fullscreenchangeEventName
    };
  } catch {
    logger.error(
      'Unable to fetch fullscreen api',
      'videoscreen.utils.getFullScreenApi'
    );
  }

  const isfullScreenSupported = false;
  return {
    requestFullScreen,
    exitFullScreen,
    isfullScreenSupported,
    browserName: name,
    fullscreenchangeEventName
  };
};

export default getVideoFullScreenDetails;

export const formatVideoDurationForSr = (durationValue) => {
  try {
    if (!durationValue || typeof durationValue !== 'string') {
      return 'Duration';
    }

    const [minutesPart = '0', secondsPart = '0'] = durationValue.split(':');
    const minutes = parseInt(minutesPart, 10);
    const seconds = parseInt(secondsPart, 10);

    if (Number.isNaN(minutes) || Number.isNaN(seconds)) {
      return `Duration ${durationValue}`;
    }

    const minutesLabel = minutes === 1 ? 'minute' : 'minutes';
    const secondsLabel = seconds === 1 ? 'second' : 'seconds';
    return `Duration ${minutes} ${minutesLabel} ${seconds} ${secondsLabel}`;
  } catch (error) {
    logger.error(
      'Failed to format video duration for screen reader',
      'VideoScreenOverlay.formatVideoDurationForSr',
      { error, durationValue }
    );
    return `Duration ${durationValue || ''}`.trim();
  }
};
